/**
 * Runtime configuration.
 *
 * BASE_PATH is the single value that decides where the app lives, and it feeds three
 * places that must never disagree: Vite's `base`, the router's `basename`, and the Express
 * mount. It is `/` since V0.48.1 — the app owns its origin, and the first thing in the path
 * is the locale (infostation.com/de/node/x) — and a path like `/infostation/` for an install
 * that shares a hostname. Nothing else in the client may hardcode either.
 *
 * Vite substitutes import.meta.env.BASE_URL at build time from the `base` option, so this
 * reads whatever the build was configured with rather than restating it.
 */
export const BASE_PATH: string = import.meta.env.BASE_URL || '/';

/**
 * BASE_PATH without its trailing slash — the `basename` parameter the location codec
 * (`lib/location.ts`) takes. Routing is a hand-rolled history controller, deliberately NOT
 * react-router (plan 1.6): the app is many roots sharing one state, so a small controller
 * owns `history` and dispatches through existing actions. The name predates that decision;
 * the export stays. `'/'` when the app is built for a host root.
 */
export const ROUTER_BASENAME: string = BASE_PATH.replace(/\/+$/, '') || '/';

/**
 * API and upload URLs are absolute from the origin root and deliberately NOT under
 * BASE_PATH: the Express routes are mounted at /api and /uploads regardless of where the
 * static app is served from.
 */
export const API_ROOT = '/api';
export const UPLOADS_ROOT = '/uploads';

/** Join a path onto the base path without doubling or dropping a slash. */
export function basePath(...parts: string[]): string {
  const tail = parts
    .map((p) => p.replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');
  return tail ? `${BASE_PATH}${tail}` : BASE_PATH;
}
